/* ═══════════════════════════════════════════════════════════════════════════
   BOARD — one-to-one with the Waveshare ESP32-P4-WIFI6-POE-ETH Rev2.0

   Geometry comes from edge-data.js, which was measured off the vendor's own
   vector assembly drawing. Nothing here invents a position. A pin lights
   because that pin is bound; a die runs hot because the die reported a
   temperature; a reading ages because the sweep that produced it has a time.

   Built from CSS 3D transforms rather than a renderer — the geometry is a
   few dozen extruded boxes, which does not justify a WebGL dependency on a
   page an operator opens forty times a shift.
   ══════════════════════════════════════════════════════════════════════════ */
import { useState, useRef, useEffect, useCallback } from "react";
import { html, Ico, Card, KV, Alert } from "./ui.js";
import { path, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import {
  BOARD, BOARD_MM, PORTS, HEADER, HOLES_MM, PIN_TRAPS, PINS_SPOKEN_FOR,
  mmBox, mmPt,
} from "./edge-data.js";
import { SERVER_ROLES } from "./board-seat-adapter.js";
import {
  bindSeat, confirmSeat, releaseSeat, overrideSeat, clearOverride,
  mountCard, unmountCard, askBoard, seatIdOfPin,
} from "./board-api.js";

/* ── silicon, in millimetres from the same drawing the ports came from ──
   Bottom-side parts (PHY, codec, amp) are drawn as ghosts so the operator
   can still address them; their opacity says they are on the other face. */
/* Soft≠Live Page30: geometry + labels only. Soft brochure °C / radio / audio
   invent deleted — Live GET …/board-telemetry owns die meters.
   Soft≠Live Page97: HUD bare Refresh → Re-read · Live · liquid board chrome. */
const SILICON = [
  { id:"p4mod", label:"ESP32-P4", sub:"P4NRW32 · bare QFN under shield · 32 MB NOR",
    ...mmBox(14.5, 34.0, 32.0, 59.0), z:3.4, mat:"shield", temp:null, side:"top",
    editable:["label"], live:null },
  { id:"flash", label:"Flash", sub:"GD25Q256EYIGR · 32 MB NOR",
    ...mmBox(16.61, 53.60, 24.23, 57.33), z:1.2, mat:"die", temp:null, side:"top",
    live:null },
  { id:"c6", label:"ESP32-C6", sub:"MINI-1U · Wi-Fi 6 + BLE 5",
    ...mmBox(18.41, 24.84, 31.61, 37.34), z:2.4, mat:"shield", temp:null, side:"top",
    /* The C6 is also a PORTS row (reached over SDIO). Same footprint, one
       identity — selecting either selects both. */
    aliasPort:"c6_sdio",
    live:null },
  { id:"t1", label:"PoE transformer", sub:"SP13 160 µH · isolated flyback",
    ...mmBox(33.64, 29.29, 47.15, 42.80), z:11, mat:"block", temp:null, side:"top",
    live:null },
  { id:"c136", label:"C136", sub:"22 µF / 100 V · PoE input",
    ...mmBox(42.75-4.2, 20.55-4.2, 42.75+4.2, 20.55+4.2), z:12, mat:"can", temp:null, side:"top",
    live:null },
  { id:"c140", label:"C140", sub:"220 µF / 16 V · PoE output",
    ...mmBox(50.89-3.3, 32.18-3.3, 50.89+3.3, 32.18+3.3), z:10, mat:"can", temp:null, side:"top",
    live:null },
  { id:"c141", label:"C141", sub:"220 µF / 16 V · PoE output",
    ...mmBox(50.89-3.3, 41.75-3.3, 50.89+3.3, 41.75+3.3), z:10, mat:"can", temp:null, side:"top",
    live:null },
  { id:"mic", label:"Microphone", sub:"MEMS · surface-mount",
    ...mmBox(49.79, 50.24, 54.21, 53.26), z:1.2, mat:"metal", temp:null, side:"top",
    live:null },
  { id:"pwrled", label:"PWR", sub:"supply indicator",
    ...mmBox(0.25, 17.63, 1.80, 19.81), z:1, mat:"die", temp:null, side:"top",
    live:null },
  { id:"rst", label:"RESET", sub:"side-actuated · restarts the node",
    ...mmBox(-0.33, 11.18, 2.18, 13.59), z:3.4, mat:"metal", temp:null, side:"top",
    action:"reset" },
  { id:"boot", label:"BOOT", sub:"side-actuated · hold to take firmware",
    ...mmBox(-0.33, 4.47, 2.18, 6.88), z:3.4, mat:"metal", temp:null, side:"top",
    action:"boot" },
  /* Bottom-side — drawn so they can be selected, dimmed so the eye knows. */
  { id:"phy", label:"IP101GRI", sub:"100 Mbps Ethernet PHY · underside",
    ...mmBox(25.07, 22.73, 29.03, 26.78), z:1.4, mat:"die", temp:null, side:"bottom",
    live:null },
  { id:"codec", label:"ES8311", sub:"audio codec · I²C 0x18 · underside",
    ...mmBox(43.11, 54.97, 46.14, 58.02), z:1.2, mat:"die", temp:null, side:"bottom",
    live:null },
  { id:"amp", label:"NS4150B", sub:"class-D amplifier · underside",
    ...mmBox(35.92, 54.01, 37.90, 58.19), z:1.2, mat:"die", temp:null, side:"bottom",
    live:null },
];

const AMBIENT = 33.4;
const heatOf = t => Math.max(0, Math.min(1, ((t == null ? AMBIENT : t) - AMBIENT) / (70 - AMBIENT)));

/* Power route in board-percentage space, measured off the same drawing:
   RJ45 top centre → PoE transformer → P4 shield. */
const POE_PATH = "M 54.4 71.0 L 54.4 55.0 L 73.5 47.1 L 42.3 32.5";
const _SWEEP_MS = 6000;
const phaseOf = s => { let h = 0; for(const c of s) h = (h*31 + c.charCodeAt(0)) % 997; return h/997; };

const PIN_TONE = {
  verified:"var(--aqua)", claimed:"var(--amber)",
  pending_confirm:"var(--amber)", fault:"var(--coral)", unassigned:"rgba(140,162,166,.5)",
};

/* Soft≠Live: Soft MODEL_JOBS / Soft tickLive / Soft tickSilicon invent deleted. */

/* ── one extruded box ───────────────────────────────────────────────────── */
const Box = ({ x, y, w, h, z, mat, tone, heat, on, dim, side, onEnter, onLeave, onClick, children }) => html`
  <div className="bx" data-mat=${mat} data-on=${on?"1":"0"} data-dim=${dim?"1":"0"}
       data-side=${side||"top"}
       style=${{ left:x+"%", top:y+"%", width:w+"%", height:h+"%",
                 "--z":z+"px", "--tone":tone||"transparent", "--heat":heat==null?0:heat }}
       onMouseEnter=${onEnter} onMouseLeave=${onLeave} onClick=${onClick}>
    <s className="bx__t">${children}</s>
    <s className="bx__s bx__s--n"/><s className="bx__s bx__s--s"/>
    <s className="bx__s bx__s--w"/><s className="bx__s bx__s--e"/>
  </div>`;

/* ═══════════════════ the board ═══════════════════════════════════════════ */
export function Board3D({
  deviceId, pins, setPins, ports, silicon,
  sel, setSel, therm, setTherm, card, onCard, toast,
  onReload, onRequestTelemetry, probeBusy, teleMeta, boardLoading,
}){
  const drag = useRef(null);
  const [rot,setRot] = useState({ x:-88, z:0 });
  const [settled,setSettled] = useState(false);
  const chips = Array.isArray(silicon) && silicon.length
    ? silicon
    : SILICON.map((c)=>({ ...c, temp:null, live:null }));

  useEffect(()=>{
    const a = setTimeout(()=> setRot({ x:-54, z:-12 }), 90);
    const b = setTimeout(()=> setSettled(true), 1350);
    return ()=>{ clearTimeout(a); clearTimeout(b); };
  },[]);

  const down = useCallback(e=>{
    drag.current = { px:e.clientX, py:e.clientY };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  },[]);
  const move = useCallback(e=>{
    const d = drag.current; if(!d) return;
    const dx = e.clientX - d.px, dy = e.clientY - d.py;
    drag.current = { px:e.clientX, py:e.clientY };
    setRot(r=>({ x:Math.max(-89, Math.min(-8, r.x - dy*0.34)), z:r.z + dx*0.42 }));
  },[]);
  const up = useCallback(e=>{ drag.current = null; e.currentTarget.releasePointerCapture?.(e.pointerId); },[]);

  const bound  = pins.filter(p=>p.role);
  const faults = pins.filter(p=>p.state==="fault");
  const overs  = pins.filter(p=>p.override);
  const livePorts = (ports || []).filter((p)=> p && p.state === "live").length;
  const teleFresh = !!(teleMeta && teleMeta.fresh);

  const portRow = id => (ports || []).find(p=>p.id===id);
  const pick = (kind,id) => setSel(sel && sel.kind===kind && sel.id===id ? null : { kind, id });
  const isOn = (kind,id) => !!(sel && sel.kind===kind && sel.id===id);

  return html`
    <div className="mb mb--liquid" data-cmc-page="page97-board" data-therm=${therm?"1":"0"} data-settled=${settled?"1":"0"}>
      <${Alert} tone="info" title=${deviceId ? "Live board - seats, ports, telemetry · Page97" : "Board geometry only · Live"}
        say=${deviceId
          ? ("Seats, ports, and die meters load from the Live board surface for " + deviceId + ". Soft brochure link and Soft °C theatre stay deleted. HUD re-reads carry · Live.")
          : "No device id — geometry is orientation only. Soft bind and Soft meters are forbidden."}/>
      ${boardLoading ? html`<p className="note">Reading Live seats / ports / telemetry…</p>` : null}
      <div className="mb__stage" onPointerDown=${down} onPointerMove=${move}
           onPointerUp=${up} onPointerCancel=${up}>
        <div className="mb__rig"
             style=${{ transform:`rotateX(${rot.x}deg) rotateZ(${rot.z}deg)` }}>

          <div className="nbb">
            <s className="nbb__s nbb__s--n"/><s className="nbb__s nbb__s--s"/>
            <s className="nbb__s nbb__s--w"/><s className="nbb__s nbb__s--e"/>
            <s className="nbb__b"/>
            <span className="nbb__silk">${BOARD.sku}<em>${BOARD_MM.w} × ${BOARD_MM.h} mm</em></span>

            ${HOLES_MM.map((h,i)=>{
              const p = mmPt(h.x, h.y);
              return html`<i key=${i} className="nbb__hole"
                style=${{ left:p.x+"%", top:p.y+"%" }}/>`;
            })}

            ${portRow("eth_poe")?.state === "live" && html`
              <svg className="nbb__poe" viewBox="0 0 100 100" preserveAspectRatio="none">
                <path d=${POE_PATH} className="poe__bed"/>
                <path d=${POE_PATH} className="poe__run"/>
              </svg>`}

            <s className="nbb__sweep"/>

            ${chips.map(c=> html`
              <${Box} key=${c.id} ...${c} tone="rgba(140,162,166,.4)"
                heat=${c.temp != null ? heatOf(c.temp) : heatOf(AMBIENT)}
                on=${isOn("si",c.id) || (c.aliasPort && isOn("port",c.aliasPort))}
                dim=${!!sel && !isOn("si",c.id) && !(c.aliasPort && isOn("port",c.aliasPort))}
                onClick=${e=>{ e.stopPropagation(); pick("si",c.id); }}>
                ${c.w < 7 ? null : html`<b className="bx__lb">${c.label}</b>`}
                ${c.live && c.live.kind==="temp" && therm && c.live.value != null && html`
                  <em className="bx__lv">${Number(c.live.value).toFixed(1)}°</em>`}
              <//>`)}

            ${PORTS.map(p=>{
              const row = portRow(p.id) || { ...p, state:"unknown", bound:null };
              const live = row.state==="live";
              const partial = row.state==="partial";
              const liveTemp = row.live && row.live.temp_c != null ? Number(row.live.temp_c) : null;
              /* The C6 is drawn as silicon above; the port row stays selectable
                 through the silicon's alias. Skip a second box on the same footprint. */
              if(p.id === "c6_sdio") return null;
              return html`
                <${Box} key=${p.id} x=${p.x} y=${p.y} w=${p.w} h=${p.h}
                  z=${p.z||4} mat=${p.id==="gpio"?"header":p.cls==="power"||p.cls==="storage"?"metal":"conn"}
                  heat=${liveTemp != null ? heatOf(liveTemp) : heatOf(AMBIENT)}
                  tone=${live?"var(--aqua)":partial?"var(--amber)":"rgba(140,162,166,.4)"}
                  on=${isOn("port",p.id)} dim=${!!sel && !isOn("port",p.id)}
                  onClick=${e=>{ e.stopPropagation(); pick("port",p.id); }}>
                  ${p.id==="gpio" || p.w < 9 ? null : html`<b className="bx__lb">${p.label}</b>`}
                <//>`;
            })}

            <div className="tf" data-state=${card}
                 onClick=${e=>{ e.stopPropagation(); pick("port","sd"); }}>
              <s className="tf__body"><s className="tf__pads"/></s>
            </div>

            ${HEADER.map(h=>{
              if(h.kind !== "gpio") return html`
                <div key=${h.seat} className="nbp" data-rail=${h.kind}
                     style=${{ left:h.x+"%", top:h.y+"%" }}
                     title=${"seat "+h.seat+" · "+h.label}>
                  <s className="nbp__post"/><s className="nbp__cap"/>
                </div>`;
              const p = pins.find(x=>x.pin===h.label) || { pin:h.label, cls:"read", state:"unassigned" };
              const tone = p.override ? "var(--ai)" : (PIN_TONE[p.state] || PIN_TONE.unassigned);
              const liveHi = p.live && p.live.kind==="digital" && p.live.value === 1;
              return html`
                <div key=${h.seat} className="nbp" data-cls=${p.cls} data-state=${p.state}
                     data-bound=${p.role?"1":"0"} data-over=${p.override?"1":"0"}
                     data-live=${liveHi?"1":"0"}
                     title=${"seat "+h.seat+" · "+h.label+(p.fn?" · "+p.fn:"")}
                     style=${{ left:h.x+"%", top:h.y+"%", "--tone":tone,
                               "--phase":(-phaseOf(p.pin)*4.2).toFixed(2)+"s" }}
                     onClick=${e=>{ e.stopPropagation(); pick("pin",p.pin); }}
                     data-on=${isOn("pin",p.pin)?"1":"0"}
                     data-reads=${p.cls==="read" && p.state==="verified" ? "1" : "0"}>
                  <s className="nbp__post"/><s className="nbp__cap"/>
                  ${p.role && html`<s className="nbp__beam"/>`}
                  ${p.live && p.state!=="unassigned" && html`
                    <b className="nbp__lv">${fmtLive(p.live)}</b>`}
                </div>`;
            })}
          </div>
        </div>
      </div>

      <div className="mb__hud">
        <div className="mb__legend">
          <span data-t="live"><i/>bound and verified</span>
          <span data-t="wait"><i/>claimed · awaiting probe</span>
          <span data-t="free"><i/>unassigned</span>
          ${overs.length>0 && html`<span data-t="ai"><i/>${overs.length} held by you</span>`}
          ${faults.length>0 && html`<span data-t="bad"><i/>${faults.length} faulted</span>`}
        </div>
        <div className="mb__tools">
          <button className="mb__tool" data-on=${therm?"1":"0"} type="button"
            title=${teleFresh ? "Show Live die temperatures on the drawing" : "Thermal needs a fresh Live telemetry frame"}
            onClick=${()=>setTherm(t=>!t)}>
            <${Ico} d="gauge" w=${13}/>Thermal · Live</button>
          <button className="mb__tool mb__tool--live" type="button"
            disabled=${!deviceId || probeBusy}
            onClick=${()=> onRequestTelemetry && onRequestTelemetry()}>
            <${Ico} d="spark" w=${13}/>${probeBusy ? "Probing · Live…" : "Request Live telemetry · Live"}</button>
          <button className="mb__tool" type="button"
            disabled=${!deviceId || boardLoading}
            onClick=${()=>{ onReload && onReload(); toast && toast("Board re-read · Live"); }}>
            <${Ico} d="refresh" w=${13}/>Re-read · Live</button>
          <button className="mb__tool" type="button" onClick=${()=>setRot({ x:-54, z:-12 })}>
            <${Ico} d="refresh" w=${13}/>Reset view · UI</button>
        </div>
      </div>
      <p className="mb__drag">${bound.length} of ${pins.length} header pins bound ·
        ${livePorts} Live port link${livePorts===1?"":"s"} ·
        telemetry ${teleFresh ? "fresh" : (teleMeta && teleMeta.age_s != null ? ("stale " + teleMeta.age_s + "s") : "none")} ·
        ${BOARD_MM.w} × ${BOARD_MM.h} mm · drag the board to turn it</p>
    </div>`;
}

const fmtLive = L => {
  if(!L) return "—";
  if(L.kind === "digital") return L.value ? "HI" : "LO";
  if(L.kind === "analogue") return L.value.toFixed(1) + (L.unit||"");
  if(L.kind === "bus") return L.value + "dev";
  if(L.kind === "radio") return L.value + "dBm";
  if(L.kind === "audio") return L.value + "dB";
  if(L.kind === "temp") return L.value.toFixed(1) + "°";
  return String(L.value);
};

/* ═══════════════════ selection → edit + AI ═══════════════════════════════ */
export function BoardDetail({ deviceId, sel, pins, setPins, ports, silicon, card, onCard, toast, onReload }){
  if(!sel) return html`
    <div className="bdet bdet--idle">
      <${Ico} d="chip" w=${17}/>
      <p>Pick a connector, a chip, or a header pin. Bind writes go Live when a device id is present.</p>
      <div className="bdet__src">
        <b>${BOARD_MM.w} × ${BOARD_MM.h} mm</b>
        <span>Geometry from the vendor assembly drawing. Port link state and die meters come from Live routes — Soft brochure values stay deleted.</span>
      </div>
    </div>`;

  if(sel.kind==="si"){
    const c = (silicon || []).find(x=>x.id===sel.id) || SILICON.find(x=>x.id===sel.id);
    if(!c) return null;
    return html`<${SiEdit} c=${c} deviceId=${deviceId} toast=${toast}/>`;
  }

  if(sel.kind==="pin"){
    const p = pins.find(x=>x.pin===sel.id); if(!p) return null;
    return html`<${PinEdit} deviceId=${deviceId} pin=${p} pins=${pins} setPins=${setPins}
      toast=${toast} onReload=${onReload}/>`;
  }

  const p = ports.find(x=>x.id===sel.id) || PORTS.find(x=>x.id===sel.id);
  if(!p) return null;
  return html`<${PortEdit} deviceId=${deviceId} port=${p} card=${card} onCard=${onCard} toast=${toast}/>`;
}

/* ── pin editor: Live W8 bind / confirm / release / override ───────────── */
function PinEdit({ deviceId, pin, pins, setPins, toast, onReload }){
  const [role,setRole] = useState(pin.role || "");
  const [ask,setAsk]   = useState("");
  const [ai,setAi]     = useState(null);
  const [busy,setBusy] = useState(false);
  const trap = pin.trap || PIN_TRAPS[pin.gpio];
  const seatId = seatIdOfPin(pin);
  const did = String(deviceId || "").trim();
  const reason = "CMC Device Board · operator " + (seatId || pin.pin || "seat");

  const liveWrite = async (fn, okMsg) => {
    if(!did){ toast("No device id — Soft bind stays forbidden", "warn"); return; }
    if(!seatId){ toast("Seat id missing for this header pin", "bad"); return; }
    setBusy(true);
    try{
      const out = await fn();
      if(!out || !out.ok){
        toast((out && out.error) || "Board write refused — nothing was sent", "bad");
        return;
      }
      toast(okMsg, "go");
      onReload && onReload();
    } finally { setBusy(false); }
  };

  const bind = ()=> liveWrite(
    ()=> bindSeat(did, seatId, { role, function_label: "", operator_reason: reason + " bind" }),
    "Seat bound · Live · " + seatId,
  );

  const unbind = ()=> liveWrite(
    ()=> releaseSeat(did, seatId),
    "Seat released · Live · " + seatId,
  );

  const confirm = ()=> liveWrite(
    ()=> confirmSeat(did, seatId, {
      expected_version: pin.version || 1,
      operator_reason: reason + " confirm",
    }),
    "Seat confirmed · Live · " + seatId,
  );

  const hold = ()=> liveWrite(
    ()=> overrideSeat(did, seatId, {
      forced_state: "isolate",
      ttl_seconds: 900,
      operator_reason: reason + (trap ? " hold trap" : " hold"),
    }),
    "Override isolate · Live · " + seatId,
  );

  const releaseHold = ()=> liveWrite(
    ()=> clearOverride(did, seatId),
    "Override cleared · Live · " + seatId,
  );

  const askAi = async ()=>{
    if(!ask.trim()) return;
    if(!did){
      toast("Ask needs a Live device — Soft handbook answers stay deleted", "warn");
      return;
    }
    setBusy(true);
    try{
      const out = await askBoard(did, { question: ask.trim(), seat_id: seatId || null });
      if(!out || !out.ok){
        toast((out && out.error) || "Board ask refused", "bad");
        return;
      }
      const say = (out.data && (out.data.answer || out.data.say || out.data.text))
        || JSON.stringify(out.data || {}).slice(0, 400);
      setAi({ say, by: "Live board/ask" });
    } finally { setBusy(false); }
  };

  const st = String(pin.state || "unassigned");
  const needsConfirm = /pending_confirm|claimed/i.test(st) && pin.cls === "execute";

  return html`
    <div className="bdet">
      <h3>${pin.pin}<em>seat ${pin.seat} · ${pin.seat_id || seatId || "—"} · gpio ${pin.gpio == null ? "null" : pin.gpio}</em></h3>

      <div className="live" data-kind=${pin.live?.kind||"none"}>
        <span className="live__v">${fmtLive(pin.live)}</span>
        <span className="live__m">
          <b>${pin.probe || "no Live reading"}</b>
          <em>${pin.liveBoard ? "Live seat" : "geometry only"}</em>
        </span>
        <i className="live__dot" data-on=${pin.liveBoard ? "1" : "0"}/>
      </div>

      <${KV} k="Bound to" v=${pin.fn || "nothing"} tone=${pin.fn?"":"dim"}/>
      <${KV} k="State" v=${st.replace(/_/g," ")}
        tone=${st==="verified"||st==="confirmed"?"go":st==="fault"?"bad":st==="unassigned"?"dim":"warn"}/>
      ${pin.override && html`
        <${KV} k="Override" v=${typeof pin.override === "object"
          ? (pin.override.forced_state || pin.override.by || "active")
          : String(pin.override)} tone="ai"/>`}

      ${trap && html`
        <div className="trap">
          <${Ico} d="alert" w=${14}/>
          <span><b>Spoken for on the board.</b> ${trap.why}</span>
        </div>`}

      ${!did ? html`<${Alert} tone="warn" title="No device id"
        say="Soft local Bind theatre is deleted. Open a real device Board tab."/>` : null}

      <div className="bedit">
        <label className="bedit__l">Role · W8</label>
        <div className="chips">
          ${SERVER_ROLES.map(r=> html`
            <button key=${r.id} className="chip" data-tone=${r.cls==="execute"?"warn":""}
                data-sel=${role===r.id?"1":"0"}
                onClick=${()=>setRole(r.id)}>${r.label}</button>`)}
        </div>
        <div className="btn-row" style=${{ marginTop:10 }}>
          <button className="btn btn--go btn--sm" disabled=${busy || !role || !did}
            onClick=${bind}>
            <${Ico} d="check" w=${13}/>${pin.role ? "Rebind · Live" : "Bind · Live"}</button>
          ${pin.role && html`
            <button className="btn btn--ghost btn--sm" disabled=${busy || !did}
              onClick=${unbind}>Unbind (Live)</button>`}
          ${needsConfirm && html`
            <button className="btn btn--ai btn--sm" disabled=${busy || !did}
              onClick=${confirm}>Confirm · arm · Live</button>`}
        </div>
      </div>

      <div className="bover">
        <div className="bover__h">
          <b>Hold / isolate · Live override</b>
          <em>POST …/pins/{seat}/override · Soft name-only holds stay deleted.</em>
        </div>
        ${pin.override ? html`
          <div className="bover__on">
            <span>Override active on <b>${seatId}</b></span>
            <button className="btn btn--ghost btn--sm" disabled=${busy || !did}
              onClick=${releaseHold}>Clear override (Live)</button>
          </div>` : html`
          <button className="btn btn--ghost btn--sm" disabled=${busy || !did}
            onClick=${hold}>
            <${Ico} d="lock" w=${13}/>Isolate seat · Live</button>`}
      </div>

      <div className="bai">
        <div className="bai__h">
          <${Ico} d="brain" w=${13}/>
          <b>Ask the board</b>
          <em>POST …/board/ask · Live</em>
        </div>
        <textarea className="bai__in" rows=${2} value=${ask}
          placeholder=${"What can I put on "+pin.pin+"?"}
          onChange=${e=>setAsk(e.target.value)}/>
        <button className="btn btn--ai btn--sm" disabled=${busy || !ask.trim() || !did}
          onClick=${askAi}>
          <${Ico} d="spark" w=${13}/>Ask (Live)</button>
        ${ai && html`
          <div className="bai__out">
            <p>${ai.say}</p>
            <em>— ${ai.by}</em>
          </div>`}
      </div>
    </div>`;
}

/* ── silicon editor ─────────────────────────────────────────────────────── */
function SiEdit({ c, deviceId, toast }){
  const [ask,setAsk] = useState("");
  const [ai,setAi]   = useState(null);
  const [busy,setBusy] = useState(false);
  const did = String(deviceId || "").trim();
  const act = useAction();

  const askAi = async ()=>{
    if(!ask.trim()) return;
    if(!did){
      toast("Ask needs a Live device — Soft die handbook stays deleted", "warn");
      return;
    }
    setBusy(true);
    try{
      const out = await askBoard(did, { question: ask.trim(), focus: c.id });
      if(!out || !out.ok){
        toast((out && out.error) || "Board ask refused", "bad");
        return;
      }
      const say = (out.data && (out.data.answer || out.data.say || out.data.text))
        || JSON.stringify(out.data || {}).slice(0, 400);
      setAi({ say, by: "Live board/ask" });
    } finally { setBusy(false); }
  };

  const pressAction = async ()=>{
    if(!did){ toast("No device id — Soft press theatre deleted", "warn"); return; }
    if(c.action === "reset"){
      const out = await act.run(path("post__devices_p_commands", { pathParams: [did] }), {
        method: "POST", body: { cmd: "reboot", params: {} },
      });
      if(out) toast("Reboot queued · Live · " + did, "warn");
      else toast(act.error || "Reboot refused", "bad");
      return;
    }
    toast(c.label + " has no Live command path — nothing was sent", "warn");
  };

  return html`
    <div className="bdet">
      <h3>${c.label}<em>${c.sub}${c.side==="bottom"?" · underside":""}</em></h3>
      ${act.envelope
        ? html`<${KV} k="Last action" v=${envelopeSummary(act.envelope) || "—"} tone=${act.envelope.ok ? "hi" : "warn"}/>`
        : null}
      ${c.live ? html`
        <div className="live" data-kind=${c.live.kind}>
          <span className="live__v">${fmtLive(c.live)}</span>
          <span className="live__m">
            <b>Live board-telemetry</b>
            <em>${c.teleFresh === false ? "stale frame" : "fresh frame"}</em>
          </span>
          <i className="live__dot" data-on=${c.teleFresh === false ? "0" : "1"}/>
        </div>` : html`
        <${Alert} tone="info" title="No Live die meter"
          say="Soft brochure °C stays deleted. Request Live telemetry or wait for a fresh board-telemetry frame."/>`}
      ${c.temp!=null && c.live && c.live.kind==="temp" && html`
        <${KV} k="Die temperature (Live)" v=${Number(c.live.value).toFixed(1)+" °C"}
          tone="go"/>`}
      <${KV} k="Side" v=${c.side==="bottom"?"underside of the board":"component side"}/>
      ${c.action && html`
        <div className="btn-row" style=${{ marginTop:10 }}>
          <button className="btn btn--ghost btn--sm" disabled=${busy || act.busy}
            onClick=${pressAction}>
            ${c.action==="reset" ? "Reboot node (Live)" : ("Press " + c.label)}</button>
        </div>`}
      <div className="bai">
        <div className="bai__h">
          <${Ico} d="brain" w=${13}/>
          <b>Ask the board</b>
          <em>POST …/board/ask · Live</em>
        </div>
        <textarea className="bai__in" rows=${2} value=${ask}
          placeholder=${"What can I do with "+c.label+"?"}
          onChange=${e=>setAsk(e.target.value)}/>
        <button className="btn btn--ai btn--sm" disabled=${busy || !ask.trim() || !did}
          onClick=${askAi}>
          <${Ico} d="spark" w=${13}/>Ask (Live)</button>
        ${ai && html`<div className="bai__out"><p>${ai.say}</p><em>— ${ai.by}</em></div>`}
      </div>
    </div>`;
}

/* ── port editor ────────────────────────────────────────────────────────── */
function PortEdit({ deviceId, port, card, onCard, toast }){
  const [ask,setAsk] = useState("");
  const [ai,setAi]   = useState(null);
  const [busy,setBusy] = useState(false);
  const did = String(deviceId || "").trim();
  const stateTone = port.state==="live"?"go":port.state==="partial"?"warn":"dim";
  return html`
    <div className="bdet">
      <h3>${port.label}<em>${port.full}</em></h3>
      <${KV} k="Live port id" v=${port.live_port_id || "geometry only"} tone=${port.live_port_id?"":"dim"}/>
      <${KV} k="Bound to" v=${port.bound || "nothing reported"} tone=${port.bound?"":"dim"}/>
      <${KV} k="State" v=${port.state || "unknown"} tone=${stateTone}/>
      ${port.lane && html`<${KV} k="Runtime lane" v=${port.lane}/>`}
      <${KV} k="Footprint" v=${port.w.toFixed(1)+"% × "+port.h.toFixed(1)+"% of "+BOARD_MM.w+"×"+BOARD_MM.h+" mm"}/>
      <p className="note">${port.detail}</p>
      ${port.honesty ? html`<p className="note">${port.honesty}</p>` : null}
      ${port.id==="sd" && html`<${TFCard} deviceId=${deviceId} card=${card} onCard=${onCard} toast=${toast}/>`}
      <div className="bai">
        <div className="bai__h"><${Ico} d="brain" w=${13}/><b>Ask the board</b><em>Live</em></div>
        <textarea className="bai__in" rows=${2} value=${ask}
          placeholder=${"What runs through "+port.label+"?"}
          onChange=${e=>setAsk(e.target.value)}/>
        <button className="btn btn--ai btn--sm" disabled=${busy || !ask.trim() || !did}
          onClick=${async ()=>{
            setBusy(true);
            try{
              const out = await askBoard(did, {
                question: ask.trim(),
                focus: port.live_port_id || port.id,
              });
              if(!out || !out.ok){ toast((out && out.error) || "Board ask refused", "bad"); return; }
              const say = (out.data && (out.data.answer || out.data.say || out.data.text))
                || "Board ask returned no answer.";
              setAi({ say, by: "Live board/ask" });
            } finally { setBusy(false); }
          }}>
          <${Ico} d="spark" w=${13}/>Ask (Live)</button>
        ${ai && html`<div className="bai__out"><p>${ai.say}</p><em>— ${ai.by}</em></div>`}
      </div>
    </div>`;
}

/* ═══════════════════ the card ════════════════════════════════════════════ */
const CARD_COPY = {
  mounted:   { t:"Mounted", s:"Live storage card mount when device routes are armed", act:"Unmount (Live)", next:"unmounting" },
  unmounting:{ t:"Unmounting…", s:"POST …/storage/card/unmount", act:null, next:"out" },
  out:       { t:"Ejected", s:"Card out — insert replacement then mount Live", act:"Mount (Live)", next:"mounting" },
  mounting:  { t:"Mounting…", s:"POST …/storage/card/mount", act:null, next:"mounted" },
};

export function TFCard({ deviceId, card, onCard, toast }){
  const c = CARD_COPY[card];
  const did = String(deviceId || "").trim();
  const [busy,setBusy] = useState(false);

  const run = async (next) => {
    if(!did){ toast("No device id — Soft TF theatre deleted", "warn"); return; }
    const reason = "CMC Device Board · TF card " + next;
    setBusy(true);
    try{
      if(next === "unmounting"){
        onCard("unmounting");
        const out = await unmountCard(did, reason);
        if(!out || !out.ok){
          onCard("mounted");
          toast((out && out.error) || "Unmount refused", "bad");
          return;
        }
        onCard("out");
        toast("TF card unmounted · Live", "warn");
        return;
      }
      if(next === "mounting"){
        onCard("mounting");
        const out = await mountCard(did, reason);
        if(!out || !out.ok){
          onCard("out");
          toast((out && out.error) || "Mount refused", "bad");
          return;
        }
        onCard("mounted");
        toast("TF card mounted · Live", "go");
      }
    } finally { setBusy(false); }
  };

  return html`
    <div className="tfp" data-state=${card}>
      <div className="tfp__h"><i/><b>${c.t}</b><span>${c.s}</span></div>
      ${c.act && html`
        <button className="btn btn--ghost btn--sm" disabled=${busy || !did}
          onClick=${()=>run(c.next)}>${c.act}</button>`}
      ${!c.act && html`
        <div className="tfp__bar" style=${{ "--dur":"1.2s" }}><s/></div>`}
      ${card==="out" && html`
        <p className="note">Live mount posts to the device storage routes. Soft timed preview transitions stay deleted.</p>`}
    </div>`;
}

/* ═══════════════════ silicon and power ═══════════════════════════════════ */
const RATED = {
  p4mod:{ cap:85, note:"SoC under load" },
  t1:   { cap:90, note:"PoE flyback" },
  phy:  { cap:85, note:"Ethernet PHY" },
  amp:  { cap:85, note:"class-D amp" },
  c6:   { cap:85, note:"Wi-Fi coprocessor" },
};

export function Silicon({ therm, pins, silicon, poe, teleMeta }){
  /* Soft≠Live Page30: Thermal / PoE only from Live board-telemetry. Soft
     brochure °C and Soft watts theatre stay deleted. */
  const chips = Array.isArray(silicon) ? silicon : [];
  const snap = chips.filter((c)=> RATED[c.id]).map((c)=>({
    k: c.label,
    v: c.temp != null ? Number(c.temp) : null,
    id: c.id,
    ...RATED[c.id],
  }));
  const livePins = (pins||[]).filter(p=>p.role);
  const teleFresh = !!(teleMeta && teleMeta.fresh);
  const poeFields = poe && poe.fields;

  return html`
    <div className="grid g2">
      <${Card} title="Thermal" meta=${teleFresh ? "Live board-telemetry" : "awaiting Live die bus"}>
        <div className="thr">
          ${snap.length === 0 && html`<p className="note">No rated dies on this geometry.</p>`}
          ${snap.map(d=> html`
            <div key=${d.k} className="thr__r"
                 data-hot=${d.v != null && d.v/d.cap>0.72?"1":"0"}
                 data-empty=${d.v == null ? "1" : "0"}>
              <span className="thr__k">${d.k}</span>
              <span className="thr__t"><s style=${{
                transform: d.v == null ? "scaleX(0)" : ("scaleX(" + (d.v/d.cap).toFixed(3) + ")")
              }}/></span>
              <b>${d.v == null ? "—" : (d.v.toFixed(1) + " °C")}</b>
              <em>${d.v == null ? "no Live meter yet" : d.note}</em>
            </div>`)}
        </div>
        <p className="note">${therm
          ? "Thermal overlay on the drawing uses Live die meters only."
          : "Soft brochure temperatures stay deleted. Request Live telemetry to fill meters."}</p>
      </${Card}>

      <${Card} title="Power over Ethernet" meta=${poe && poe.fresh ? "Live PoE PD" : "awaiting Live meter"}>
        <${KV} k="Transformer" v="T1 · SP13 160 µH · drawing"/>
        <${KV} k="Supply"
          v=${poeFields && poeFields.watts != null ? (poeFields.watts + " W") : "—"}
          tone=${poeFields && poeFields.watts != null ? "go" : "dim"}/>
        <${KV} k="IEEE class"
          v=${poeFields && poeFields.classN != null ? String(poeFields.classN) : "—"}
          tone=${poeFields && poeFields.classN != null ? "" : "dim"}/>
        <${KV} k="Vin"
          v=${poeFields && poeFields.vin != null ? (poeFields.vin + " V") : "—"}
          tone=${poeFields && poeFields.vin != null ? "" : "dim"}/>
        <p className="note">Soft brochure watts / uptime stay deleted. Empty means the Live frame has no PoE meters yet.</p>
      </${Card}>

      <${Card} title="Board pins" meta=${livePins.length+" Live-bound seats"}
          sub="Hydrated from GET …/pins. Soft brochure bindings stay deleted.">
        <div className="lpins">
          ${livePins.length===0 && html`<p className="note">Nothing is bound on Live yet.</p>`}
          ${livePins.map(p=> html`
            <div key=${p.pin} className="lpins__r" data-cls=${p.cls}
                 data-over=${p.override?"1":"0"}>
              <code>${p.pin}</code>
              <span>${p.fn}</span>
              <b>${p.state}</b>
              <em>${p.seat_id || "—"}</em>
            </div>`)}
        </div>
      </${Card}>

      <${Card} title="Already wired on the board" meta="never invent GPIO">
        <div className="spoken">
          ${PINS_SPOKEN_FOR.map(s=> html`
            <div key=${s.fn} className="spoken__r" data-trap=${s.trap?"1":"0"}>
              <span>${s.fn}${s.trap?html`<i> · ${s.trap}</i>`:null}</span>
              <code>${s.pins}</code>
            </div>`)}
        </div>
      </${Card}>
    </div>`;
}

export { SILICON, AMBIENT, heatOf };
